import React, { useEffect, useState } from 'react';
import { X, Search, Check, Star, StarOff, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import { useSellerProductSearch, mergeProducts } from '@/lib/useSellerProductSearch';

/**
 * Reusable modal: seller picks the products to sell during a broadcast.
 *
 * Props:
 *   broadcastId — schedule_id of the broadcast (owner-only enforced by API).
 *   onClose()
 *   onSaved()     — called after successful PUT.
 *
 * Uses only the current seller's products (backend filters by ownership).
 * Never exposes other sellers' inventory.
 */
const BroadcastProductPicker = ({ broadcastId, onClose, onSaved }) => {
  const [products, setProducts] = useState([]);           // seller's own products
  const [linked, setLinked]     = useState([]);           // [{product_id, is_featured, sort_order}]
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ownRes, linksRes] = await Promise.all([
          api.get('/seller/products').catch(() => ({ data: [] })),
          broadcastId ? api.get(`/seller/broadcasts/${broadcastId}/products`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;
        const own = Array.isArray(ownRes.data) ? ownRes.data : [];
        const links = linksRes.data || [];
        // LIVE-SELLER-RECORDING-TEST-FLOW-1 · 기본 목록(500개)에 없는 연결 상품도 이름 표시.
        const missing = links.map((lk) => lk.product_id).filter((pid) => !own.some((p) => p.product_id === pid));
        const extra = (await Promise.all(missing.map((pid) => api.get(`/seller/products/${pid}`).then((r) => r.data).catch(() => null)))).filter(Boolean);
        if (cancelled) return;
        setProducts(mergeProducts(own, extra));
        setLinked(links.map((lk) => ({
          product_id: lk.product_id, is_featured: !!lk.is_featured, sort_order: lk.sort_order ?? 0,
        })));
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [broadcastId]);

  const found = useSellerProductSearch(q);
  useEffect(() => { if (found.length) setProducts((prev) => mergeProducts(prev, found)); }, [found]);
  const filtered = products.filter((p) => !q || (p.name || '').toLowerCase().includes(q.toLowerCase()));
  const isChecked = (pid) => linked.some((x) => x.product_id === pid);
  const isFeatured = (pid) => linked.find((x) => x.product_id === pid)?.is_featured;

  const toggle = (pid) => setLinked((arr) => {
    const has = arr.find((x) => x.product_id === pid);
    if (has) return arr.filter((x) => x.product_id !== pid);
    return [...arr, { product_id: pid, is_featured: false, sort_order: arr.length }];
  });
  const toggleFeatured = (pid) => setLinked((arr) => arr.map((x) => x.product_id === pid ? { ...x, is_featured: !x.is_featured } : x));
  const moveUp = (idx) => setLinked((arr) => {
    if (idx <= 0) return arr;
    const next = arr.slice();
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    return next.map((x, i) => ({ ...x, sort_order: i }));
  });
  const moveDown = (idx) => setLinked((arr) => {
    if (idx >= arr.length - 1) return arr;
    const next = arr.slice();
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    return next.map((x, i) => ({ ...x, sort_order: i }));
  });
  const selectAll = () => setLinked(filtered.map((p, i) => ({ product_id: p.product_id, is_featured: false, sort_order: i })));
  const clearAll  = () => setLinked([]);

  const save = async () => {
    if (!broadcastId) return;
    setSaving(true);
    try {
      const payload = { products: linked.map((x, i) => ({ ...x, sort_order: i })) };
      await api.put(`/seller/broadcasts/${broadcastId}/products`, payload);
      toast.success('방송 상품이 저장되었습니다.');
      onSaved?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || '방송 상품 저장에 실패했습니다. 다시 시도해주세요.';
      toast.error(String(detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bp-picker">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/5 dark:border-white/10">
          <div>
            <h3 className="font-black text-lg">방송 판매 상품</h3>
            <p className="text-[11px] text-gray-500">선택 {linked.length}개 · 본인 상품만 노출됩니다.</p>
          </div>
          <button onClick={onClose} className="p-1" aria-label="닫기"><X size={16} /></button>
        </div>

        <div className="px-5 py-3 border-b border-black/5 dark:border-white/10 flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="상품명 검색"
              className="w-full pl-8 pr-3 py-1.5 border rounded-full text-sm bg-white dark:bg-[#141414] border-black/10 dark:border-white/10" data-testid="bp-search" />
          </div>
          <button onClick={selectAll} className="text-xs font-bold text-[#FF3B30]" data-testid="bp-select-all">전체 선택</button>
          <button onClick={clearAll} className="text-xs font-bold text-gray-500" data-testid="bp-clear-all">선택 해제</button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? <p className="p-6 text-center text-sm text-gray-500">불러오는 중…</p> :
           filtered.length === 0 ? <p className="p-6 text-center text-sm text-gray-500">상품이 없습니다.</p> :
           (
            <ul className="divide-y divide-black/5 dark:divide-white/10">
              {filtered.map((p) => (
                <li key={p.product_id} className="flex items-center gap-3 py-2" data-testid={`bp-row-${p.product_id}`}>
                  <input type="checkbox" checked={isChecked(p.product_id)} onChange={() => toggle(p.product_id)} data-testid={`bp-check-${p.product_id}`} />
                  <SafeImg src={p.image} kind="product" alt={p.name} className="w-10 h-10 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{p.name}</div>
                    <div className="text-[11px] text-gray-500">{(p.sale_price || 0).toLocaleString('ko-KR')}원 · {p.sale_status}</div>
                  </div>
                  {isChecked(p.product_id) && (
                    <button type="button" onClick={() => toggleFeatured(p.product_id)}
                      className={`p-1 rounded ${isFeatured(p.product_id) ? 'text-[#FF3B30]' : 'text-gray-400 hover:text-gray-600'}`}
                      title={isFeatured(p.product_id) ? '대표 해제' : '대표 지정'} data-testid={`bp-feature-${p.product_id}`}>
                      {isFeatured(p.product_id) ? <Star size={16} fill="currentColor" /> : <StarOff size={16} />}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {linked.length > 0 && (
          <div className="border-t border-black/5 dark:border-white/10 p-3 max-h-40 overflow-y-auto">
            <div className="text-[11px] font-bold text-gray-500 mb-2">노출 순서 (위쪽이 먼저)</div>
            <ol className="text-xs space-y-1">
              {linked.map((lk, idx) => {
                const p = products.find((x) => x.product_id === lk.product_id);
                if (!p) return null;
                return (
                  <li key={lk.product_id} className="flex items-center gap-2">
                    <span className="w-5 text-gray-400">{idx + 1}.</span>
                    <span className="flex-1 truncate">{p.name}</span>
                    <button onClick={() => moveUp(idx)} disabled={idx === 0} className="text-xs px-1.5 py-0.5 disabled:opacity-30" data-testid={`bp-up-${lk.product_id}`}>▲</button>
                    <button onClick={() => moveDown(idx)} disabled={idx === linked.length - 1} className="text-xs px-1.5 py-0.5 disabled:opacity-30" data-testid={`bp-down-${lk.product_id}`}>▼</button>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        <div className="flex justify-end gap-2 p-4 border-t border-black/5 dark:border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold bg-black/5 dark:bg-white/10">취소</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-bold bg-[#FF3B30] text-white disabled:opacity-60" data-testid="bp-save">
            <Save size={12} /> {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BroadcastProductPicker;
